import React, { useContext, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useMachine } from '@xstate/react';
import shortid from 'shortid';
import {
    PageContext,
    CommunityContext,
} from '../../../contexts/ContextCreator';
import unwrapMentionBlot from '../Modules/Mention/Mention';
import ButtonPanel from '../ButtonPanel/ButtonPanel';
import PostBoxCTA from '../PostBoxCTA/PostBoxCTA';
import postBoxMachine from './machine/postBoxMachine';
import useOnClickOutside from '../../../utilities/hooks/useOnClickOutside';
import { constructCn } from '../../../utilities/helperFunctions';
import { getProp, autoformatOpts } from './utils';
import { EmitMetric } from '../../Analytics/VisibilityPixel/VisibilityPixel';
import './PostBoxContainer.scss';

const Quill =
    typeof window === 'object' ? require('react-quill').Quill : () => false;

const ReactQuill =
    typeof window === 'object' ? require('react-quill') : () => false;
const AutoFormat =
    typeof window === 'object'
        ? require('quill-autoformat').default
        : () => false;

const placeHolders = [
    'Share anything (even anonymously) ...',
    'Ask anything (even anonymously) ...',
    "What's on your mind today?",
];

const PostBoxContainer = ({
    pageGroup = {},
    title = '',
    description = '',
    editMode = false,
    handleOnEditCanceled = () => {},
    hash = null,
    isAnon = false,
    isComment = false,
    type = null,
    image = null,
    commentDepth = 0,
    url = null,
}) => {
    const {
        updateContentFromUPBResponse,
        defaultPostLocation,
        editCommunityContent,
        sort,
    } = useContext(CommunityContext);
    const { pageProps: { session: { id: userId = null } } = {} } = useContext(
        PageContext
    );
    const [postBoxFocused, setPostBoxFocused] = useState(false);
    const sortTypeRef = useRef(sort);
    const inputRef = useRef();
    useEffect(() => {
        sortTypeRef.current = sort;
    }, [sort]);
    const initial = editMode ? 'open' : 'closed';
    let postBoxId = 'postbox';
    if (hash) postBoxId += `-${hash}`;
    const newMachine = postBoxMachine.withContext({
        value: initial,
        initial,
        id: postBoxId,
    });
    const [state, send] = useMachine(newMachine, {
        context: {
            communityTopicId: defaultPostLocation?.id ?? null,
            updateCommunityContext: updateContentFromUPBResponse,
            sortTypeRef,
            isAnonymousPost: !!isAnon,
            isComment,
            hash: hash ?? null,
            editCommunityContent,
            contentType: type ?? null,
            link: { url },
            image: image?.src,
            editMode,
        },
    });

    const [prompt, setPrompt] = useState(null);
    const [quill, setQuill] = useState(null);
    let curLinks = [];
    const closeHandler = () => {
        curLinks = [];
    };
    useEffect(() => {
        if (!editMode) {
            setPrompt(
                placeHolders[
                    Math.floor(Math.random() * Math.floor(placeHolders.length))
                ]
            );
        } else send('OPEN');
    }, [editMode, send]);

    const handleCtaClick = React.useCallback(() => {
        // dispatchinsertHashtag open event
        send('OPEN');

        if (pageGroup.hash) {
            send('SELECT_POST_GROUP', { value: pageGroup.hash });
        }

        // emit metric
        const openMetric = {
            misc_event_type: 'postbox-cta-click',
            misc_event_count: 1,
            ...(userId ? { user_id: userId } : {}),
        };

        setPostBoxFocused(true);
        EmitMetric(openMetric);
    }, [userId, send, pageGroup.hash]);

    const handlePostClick = React.useCallback(() => {
        send('PUBLISH');
    }, [send]);

    const containerRef = useOnClickOutside(() => {
        // clear focus on editor to collapse keyboard for IOS devices

        const isFocused = document.activeElement.className.includes(
            'ql-editor'
        );
        if (isFocused) {
            document.activeElement.blur();
        }

        if (!state.context.editMode) send('CLOSE');
    });

    useEffect(() => {
        if (inputRef.current) {
            const curQuill = inputRef.current.getEditor();
            curQuill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
                // eslint-disable-next-line no-param-reassign
                delta.ops = delta.ops.map((op) => {
                    return {
                        insert: op.insert.replace(/\u00a0/g, ' '),
                    };
                });
                return delta;
            });
            setQuill(curQuill);
        }
    }, [inputRef]);

    // event handlers
    const handleCancelClick = React.useCallback(() => {
        send('CANCEL');
        handleOnEditCanceled(false);
    }, [send, handleOnEditCanceled]);

    const handleUpdateClick = React.useCallback(() => {
        send('UPDATE');
        handleOnEditCanceled(true);
    }, [send, handleOnEditCanceled]);

    const handleTogglePostAnon = React.useCallback(() => {
        if (state.value === 'closed' && editMode) send('OPEN');
        send('TOGGLE_POST_ANONYMITY');
    }, [send, editMode, state.value]);

    const handleBlur = React.useCallback(() => {}, []);

    const handleChange = React.useCallback(
        ({ links, prevLinks, html, postText }) => {
            const update = {
                postText,
                html,
                links: links.length > 0 ? links : prevLinks,
            };
            send('EDITOR_UPDATE', update);
            // @TODO dispatch an event that clears the hash.

            // TODO: fix dirty/clean logic
            // this only fires with an empty editor
            // so its not truly 'dirty' i.e. user has made changes
            if (postText.length === 0) {
                send('EDITOR_CLEAN');
            }
        },

        [send]
    );

    const handleFocus = React.useCallback(() => send('ACTIVE'), [send]);

    useEffect(() => {
        if (!quill) {
            return;
        }

        if (state.context.postSuccess) {
            quill.root.innerHTML = '';
        }
    }, [quill, state.context.postSuccess]);

    const autoFocusEditor = React.useCallback(() => {
        if (!quill || quill.hasFocus()) return;

        quill.focus();
    }, [quill]);

    useEffect(() => {
        if (state?.value === 'closed') {
            setPostBoxFocused(false);
        } else if (state?.value === 'open') {
            setPostBoxFocused(true);
            autoFocusEditor();
        }
    }, [state, autoFocusEditor]);

    useEffect(() => {
        if (!quill) {
            return;
        }
        quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
            // eslint-disable-next-line no-param-reassign
            delta.ops = delta.ops.map((op) => {
                let insert = '';

                if (
                    typeof op.insert === 'object' &&
                    op.insert !== null &&
                    op.insert.hashtag !== undefined
                ) {
                    insert = op.insert.hashtag;
                }

                if (typeof op.insert === 'string') {
                    insert = op.insert.replace(/\u00a0/g, ' ');
                }

                return {
                    insert,
                };
            });
            return delta;
        });
        let typingTimer;
        const doneTypingInterval = 10;
        // this interval is used to prevent update loops with quill.
        const doneTyping = () => {
            const currentContents = quill.getContents();
            let linkIndex = 0;
            let updateMade = false;
            let retainLength = 0;
            const newDelta = [];
            // eslint-disable-next-line array-callback-return
            currentContents.ops.map((op) => {
                const nextOps = currentContents.ops[linkIndex + 1];
                if (op.attributes?.link) {
                    const isLinkFormat = /https?:\/\/[\S]+|(www\.[\S]+)/gi.test(
                        op.insert
                    );
                    if (nextOps && isLinkFormat) {
                        const newData = nextOps.insert;
                        const hasStartingWhiteSpace = /^\s+/.test(newData);
                        const hasEndingWhiteSpace = /\s$/.test(op.insert);
                        if (!hasStartingWhiteSpace && !hasEndingWhiteSpace) {
                            const dataSplits = newData.split(/[.!?\n\r\s]/gs);
                            const firstSection = newData.substring(
                                0,
                                dataSplits[0].length
                            );
                            const newLink = op.insert + firstSection;
                            if (retainLength)
                                newDelta.push({ retain: retainLength });
                            newDelta.push({ delete: op.insert.length });
                            newDelta.push({
                                insert: newLink,
                                attributes: { link: newLink },
                            });
                            newDelta.push({ delete: firstSection.length });
                            if (newData.length - firstSection.length)
                                newDelta.push({
                                    retain:
                                        newData.length - firstSection.length,
                                });
                            retainLength = 0;
                            updateMade = true;
                        }
                    } else if (op.attributes?.link !== op.insert) {
                        if (retainLength)
                            newDelta.push({ retain: retainLength });
                        if (isLinkFormat) {
                            newDelta.push({ delete: op.insert.length });
                            newDelta.push({
                                insert: op.insert,
                                attributes: { link: op.insert },
                            });
                        } else {
                            if (currentContents.ops[linkIndex - 1])
                                newDelta.push({
                                    retain:
                                        currentContents.ops[linkIndex - 1]
                                            .insert.length,
                                });
                            newDelta.push({ delete: op.insert.length });
                            newDelta.push({
                                insert: op.insert,
                            });
                        }
                        retainLength = 0;
                        updateMade = true;
                    }
                } else {
                    retainLength += op.insert.length;
                }
                linkIndex += 1;
            });
            if (updateMade) {
                if (retainLength) newDelta.push({ retain: retainLength });
                quill.updateContents(newDelta);
            }
            let postText = quill.getText(0).trim();
            postText = postText.replace('\n', '\n ');
            quill.formatText(0, postText.length + 1, 'bold', false);
            const splits = postText.split(/\n/);
            const lineformat = quill.getFormat(0, splits[0].length);
            if (!lineformat.bold && splits.length > 1) {
                const trueLength = postText.length - splits[1].length - 1;
                const firstSection = postText.substring(0, trueLength + 1);
                const hasEndingPunction = /[.!?]$/.test(firstSection);
                if (!firstSection.includes('http') && !isComment) {
                    let extraSpaceNeeded = 0;
                    if (hasEndingPunction) extraSpaceNeeded = 1;
                    quill.formatText(
                        0,
                        splits[0].length + extraSpaceNeeded,
                        'bold',
                        true
                    );
                }
            } else if (
                !postText.includes('http') &&
                !isComment &&
                type !== 'community-photo' &&
                type !== 'community-link'
            )
                quill.formatText(0, postText.length + 1, 'bold', true);
        };
        // eslint-disable-next-line no-unused-vars
        quill.root.addEventListener('keydown', (evt) => {
            clearTimeout(typingTimer);
        });
        // eslint-disable-next-line no-unused-vars
        quill.root.addEventListener('keyup', (evt) => {
            clearTimeout(typingTimer);
            typingTimer = setTimeout(doneTyping, doneTypingInterval);
        });
        const onEditorChange = (event, _delta, _oldDelta) => {
            const currentContents = quill.getContents();
            const postText = quill.getText(0).trim();
            const postTextLength = postText.length;

            const isBlurEvent = event === 'selection-change' && _delta === null;
            const isFocusEvent =
                event === 'selection-change' &&
                _delta !== null &&
                _oldDelta === null;
            const isChangeEvent = event === 'text-change';

            const blurOrFocusPayload = {
                currentContents,
                textLength: postTextLength,
            };

            if (isBlurEvent && quill) {
                handleBlur(blurOrFocusPayload);
            }

            if (isFocusEvent && quill) {
                handleFocus(blurOrFocusPayload);
            }

            if (isChangeEvent && quill) {
                if (state.context?.error?.length) {
                    send('CLEAR_ERROR');
                }
                let links = [url] || [];

                if (!editMode) {
                    links = currentContents.ops
                        .map((op) => getProp(op, 'attributes.link'))
                        .filter(Boolean);
                    if (links.length > 0) curLinks = links;
                }

                handleChange({
                    links,
                    prevLinks: curLinks,
                    postText,
                    html: quill.root.innerHTML,
                    textLength: postTextLength,
                });
            }
        };
        quill.on('editor-change', onEditorChange);
        // set values based on existing descrption
        if (title && description) {
            quill.setText(`${title}\n${description}`, 'user');
            doneTyping();
        } else if (title) {
            quill.setText(`${title}`, 'user');
            doneTyping();
        } else if (description) {
            quill.setText(`${description}`, 'user');
            doneTyping();
        }
        return () => {
            if (quill) {
                quill.off('editor-change', onEditorChange);
            }
        };
    }, [
        quill,
        send,
        handleBlur,
        handleChange,
        handleFocus,
        state.context.error,
    ]);

    const insertHashtag = React.useCallback(() => {
        if (quill) {
            if (!quill.hasFocus()) quill.focus();

            const range = quill.getSelection();

            const isCollapsed = range?.length === 0;
            if (isCollapsed) {
                quill.insertText(range.index, '#', 'silent');
                quill.setSelection(range.index + 1, 0, 'silent');
            }
        }
        const hashtagMetric = {
            misc_event_type: 'editor-hashtag-click',
            misc_event_count: 1,
            ...(userId ? { user_id: userId } : {}),
        };

        EmitMetric(hashtagMetric);
    }, [quill, userId]);

    const postButtonDisabled =
        state.value === 'closed' ||
        !state.context.isDirty ||
        state.context.isInactive ||
        !userId; // disable posting for unauthed users
    const editmodeClass = constructCn(
        editMode && 'editMode',
        isComment && 'commentMode'
    );
    const editorContainerClass = constructCn(
        'component-UnifiedPostBox-PostBoxEditor',
        editMode && 'editMode',
        isComment && 'commentMode',
        !postBoxFocused && 'hidden-editor'
    );
    const postBoxContainerClass = constructCn(
        'component-UnifiedPostBox-PostBoxContainer',
        editMode && 'editMode',
        isComment && 'commentMode',
        commentDepth > 2 && 'deepComment'
    );
    const disablePhoto =
        state.context.link?.url?.length > 0 || state.context.image?.length > 0;
    let UnifiedPostBoxId = 'unified-post-box';
    if (hash) UnifiedPostBoxId += `-${hash}`;
    let modules = {};
    if (ReactQuill && Quill.register) {
        const Embed = Quill.import('blots/embed');
        Quill.register('modules/autoFormat', AutoFormat, true);
        const Mention = unwrapMentionBlot(Embed);
        Quill.register('formats/mention', Mention, true);
        modules = {
            toolbar: false,
            autoFormat: autoformatOpts,
        };
    }
    return (
        <div
            className={`${postBoxContainerClass} ${
                postBoxFocused ? 'animated-post-box' : ''
            }`}
            id={UnifiedPostBoxId}
            ref={containerRef}
        >
            {/**
             * The keyboard would not be triggered on IOS devices when auto-focus with our text-editor
             * On IOS devices, html element has to satisfy the following conditions for keyboard to be triggered when focus
             *     1. editable html element, i.e: input, textarea
             *     2. present on screen, i.e can't have "display: none" property
             * Work Around:
             * Placing an invisible input element as placeholder for focusing and triggering keyboard
             * Focus will be first drawn to this mock input, then re-focus to our text-editor
             */}
            <input type="text" id="mock-input-for-ios-focus" />
            {!editMode && <div className="post-box-top-accent" />}
            <div className="post-box-main-content">
                {!postBoxFocused && (
                    <PostBoxCTA
                        onClick={handleCtaClick}
                        placeholderText={prompt}
                    />
                )}
                <div className={editorContainerClass}>
                    <form>
                        <ReactQuill
                            className={editmodeClass}
                            ref={inputRef}
                            name="commentBoxInput"
                            placeholder={prompt}
                            modules={modules}
                            theme="snow"
                        />
                    </form>
                </div>
                {postBoxFocused && (
                    <div className="post-box__button-panel-wrapper">
                        <ButtonPanel
                            closeHandler={closeHandler}
                            dispatch={send}
                            hash={hash}
                            commentDepth={commentDepth}
                            saveButtonDisabled={postButtonDisabled}
                            editMode={editMode}
                            handleCancelClick={handleCancelClick}
                            handleUpdateClick={handleUpdateClick}
                            handleTogglePostAnon={handleTogglePostAnon}
                            insertHashtag={insertHashtag}
                            isAnonymousPost={state.context.isAnonymousPost}
                            communityGroupHash={
                                state.context.communityGroupHash
                            }
                            disablePhoto={disablePhoto}
                            pageGroup={pageGroup}
                            embedlyUrl={state.context.link.url}
                            previewDisabled={state.context.isPreviewDisabled}
                            image={state.context.image}
                        />
                    </div>
                )}
            </div>
            {state.context.uploadError?.length > 0 && (
                <div className="post-error-message">
                    {state.context.uploadError.map((err) => (
                        <span key={shortid()}>{err}</span>
                    ))}
                </div>
            )}
            {state.matches('error') && state.context?.error?.length && (
                <div className="post-error-message">
                    {state.context.error.map((err) => (
                        <span key={shortid()}>{err}</span>
                    ))}
                </div>
            )}
            {!editMode && (
                <button
                    type="button"
                    disabled={postButtonDisabled}
                    onClick={handlePostClick}
                    className="post-button"
                >
                    Post
                </button>
            )}
        </div>
    );
};

PostBoxContainer.propTypes = {
    pageGroup: PropTypes.shape({
        hash: PropTypes.string,
        name: PropTypes.string,
    }),
    handleOnEditCanceled: PropTypes.func,
    description: PropTypes.string,
    editMode: PropTypes.bool,
    isComment: PropTypes.bool,
    isAnon: PropTypes.bool,
    title: PropTypes.string,
    hash: PropTypes.string,
    type: PropTypes.string,
    image: PropTypes.string,
    url: PropTypes.string,
    commentDepth: PropTypes.number,
};

PostBoxContainer.defaultProps = {
    pageGroup: {},
    description: null,
    handleOnEditCanceled: () => {},
    editMode: false,
    title: null,
    type: null,
    isAnon: false,
    hash: '',
    image: null,
    url: null,
    isComment: false,
    commentDepth: 0,
};

PostBoxContainer.displayName = 'PostBoxContainer';

export default PostBoxContainer;
